import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  PlusOutlined,
  DeleteOutlined,
  EditOutlined,
  SaveOutlined,
  CloseOutlined,
} from '@ant-design/icons';
import {
  Form,
  Input,
  InputNumber,
  Select,
  Switch,
  Button,
  Divider,
  Space,
  Table,
  Popconfirm,
  DatePicker,
  message,
  Spin,
  Card,
  Tag,
  Progress,
} from 'antd';
import dayjs from 'dayjs';
import { useCompanyConfig, useUpdateCompanyConfig } from './_shared/hooks';
import CompanyDataLayerConfig from './_shared/CompanyDataLayerConfig';
import MonthParamsCard from './_shared/MonthParamsCard';
import OtherPaymentsSectorOverrides from './_shared/OtherPaymentsSectorOverrides';
import { computeConfigCompleteness } from './_shared/configCompleteness';

const CALC_TYPE_KEYS = [
  { value: 'TYPE_01_FIXED_RSD', key: 'ozCalcType01' },
  { value: 'TYPE_02_POINTS', key: 'ozCalcType02' },
  { value: 'TYPE_05_POINTS_INPUT', key: 'ozCalcType05' },
  { value: 'TYPE_07_ADVANCE', key: 'ozCalcType07' },
  { value: 'TYPE_08_POINTS_NO_HOURS', key: 'ozCalcType08' },
  { value: 'TYPE_11_PREV_MONTH', key: 'ozCalcType11' },
  { value: 'TYPE_40_AVG_3M', key: 'ozCalcType40' },
  { value: 'TYPE_41_AVG_CONTRACT', key: 'ozCalcType41' },
  { value: 'TYPE_47_MATERNITY_12M', key: 'ozCalcType47' },
  { value: 'TYPE_62_BASE_LIMIT', key: 'ozCalcType62' },
];

const TAX_TREATMENT_KEYS = [
  { value: 'FULLY_TAXABLE', key: 'ozTaxFull' },
  { value: 'NON_TAXABLE', key: 'ozTaxNone' },
  { value: 'PARTIALLY_TAXABLE', key: 'ozTaxPartial' },
];

const LIMIT_TYPE_KEYS = [
  { value: 'NONE', key: 'ozLimitNone' },
  { value: 'ONE_THIRD_NET', key: 'ozLimitOneThird' },
  { value: 'TWO_THIRDS_NET', key: 'ozLimitTwoThirds' },
  { value: 'CUSTOM_PERCENT', key: 'ozLimitCustom' },
];

const CATEGORY_KEYS = [
  { value: 'MANDATORY', key: 'ozCatMandatory' },
  { value: 'VOLUNTARY', key: 'ozCatVoluntary' },
];

const AB_SOFT_TYPE_KEYS = [
  { value: 0, key: 'ozAbType0' },
  { value: 1, key: 'ozAbType1' },
  { value: 3, key: 'ozAbType3' },
];

const CALC_DIRECTION_KEYS = [
  { value: 'GROSS_TO_NET', key: 'ozDirGrossToNet' },
  { value: 'NET_TO_GROSS', key: 'ozDirNetToGross' },
];

// Currencies actually in use across the platform's companies, measured
// 2026-08-07: EUR at 24 companies, RUB at OZON, USD at the Indian embassy.
// The rest are here so a new company does not need a code change.
const CURRENCY_OPTIONS = ['EUR', 'USD', 'RUB', 'CHF', 'GBP', 'CNY'].map((c) => ({
  value: c,
  label: c,
}));

const POINT_VALUE_SOURCE_KEYS = [
  { value: 'FIXED_RSD', key: 'ozPvsFixedRsd' },
  { value: 'FIXED_EUR', key: 'ozPvsFixedEur' },
  { value: 'NBS_RATE', key: 'ozPvsNbsRate' },
];

const WORKING_HOURS_KEYS = [
  { value: 'CALENDAR', key: 'ozWhCalendar' },
  { value: 'FIXED_176', key: 'ozWh176' },
  { value: 'FIXED_168', key: 'ozWh168' },
  { value: 'CUSTOM', key: 'ozWhCustom' },
];

const ROUNDING_KEYS = [
  { value: 'ROUND', key: 'ozRoundRound' },
  { value: 'FLOOR', key: 'ozRoundFloor' },
  { value: 'CEIL', key: 'ozRoundCeil' },
];

const DEDUCTION_ORDER_KEYS = [
  { value: 'BY_PRIORITY', key: 'ozDeductByPriority' },
  { value: 'PROPORTIONAL', key: 'ozDeductProportional' },
];

const OVERTIME_BASE_KEYS = [
  { value: 'GROSS', key: 'ozOvertimeBaseGross' },
  { value: 'NET', key: 'ozOvertimeBaseNet' },
];

const AVG_METHOD_KEYS = [
  { value: 'HOURLY_RATE', key: 'ozAverageHourly' },
  { value: 'MONTHLY_AVG', key: 'ozAverageMonthly' },
];

function translateOptions(keys, t) {
  return keys.map((k) => ({
    value: k.value,
    label: t(k.key),
  }));
}

function ArrayCrud({
  title,
  items,
  onAdd,
  onRemove,
  columns,
  emptyItem,
  editingIndex,
  setEditingIndex,
  editForm,
  onSaveEdit,
  confirmText,
}) {
  return (
    <Card
      size="small"
      title={title}
      extra={
        <Button
          size="small"
          icon={<PlusOutlined />}
          onClick={() => onAdd(emptyItem)}
        />
      }
      className="mb-4"
    >
      <Table
        dataSource={items}
        columns={[
          ...columns.map((col) => ({
            ...col,
            render: (val, record, idx) => {
              if (idx === editingIndex && col.editable) {
                return col.editRender(val, record, idx);
              }
              return col.render ? col.render(val, record, idx) : val;
            },
          })),
          {
            title: '',
            key: 'actions',
            width: 80,
            render: (_, record, idx) =>
              idx === editingIndex ? (
                <Space>
                  <Button
                    size="small"
                    type="primary"
                    icon={<SaveOutlined />}
                    onClick={() => onSaveEdit(idx)}
                  />
                  <Button
                    size="small"
                    icon={<CloseOutlined />}
                    onClick={() => setEditingIndex(null)}
                  />
                </Space>
              ) : (
                <Space>
                  <Button
                    size="small"
                    icon={<EditOutlined />}
                    onClick={() => {
                      setEditingIndex(idx);
                      editForm.setFieldsValue(record);
                    }}
                  />
                  <Popconfirm
                    title={confirmText}
                    onConfirm={() => onRemove(idx)}
                  >
                    <Button size="small" danger icon={<DeleteOutlined />} />
                  </Popconfirm>
                </Space>
              ),
          },
        ]}
        rowKey={(_, idx) => idx}
        size="small"
        pagination={false}
      />
    </Card>
  );
}

export default function OzCompanyConfig({ companyId }) {
  const { t } = useTranslation(['forms']);
  const [form] = Form.useForm();
  const [earningEditForm] = Form.useForm();
  const [deductionEditForm] = Form.useForm();
  const [otherPaymentEditForm] = Form.useForm();

  const { data: config, isLoading } = useCompanyConfig(companyId);
  const updateConfig = useUpdateCompanyConfig();
  const workingHoursSource = Form.useWatch('workingHoursSource', form);

  const calcTypeOptions = useMemo(
    () => translateOptions(CALC_TYPE_KEYS, t),
    [t]
  );
  const taxTreatmentOptions = useMemo(
    () => translateOptions(TAX_TREATMENT_KEYS, t),
    [t]
  );
  const categoryOptions = useMemo(
    () => translateOptions(CATEGORY_KEYS, t),
    [t]
  );
  const abSoftTypeOptions = useMemo(
    () => translateOptions(AB_SOFT_TYPE_KEYS, t),
    [t]
  );
  const limitTypeOptions = useMemo(
    () => translateOptions(LIMIT_TYPE_KEYS, t),
    [t]
  );
  const calcDirectionOptions = useMemo(
    () => translateOptions(CALC_DIRECTION_KEYS, t),
    [t]
  );
  const pointValueSourceOptions = useMemo(
    () => translateOptions(POINT_VALUE_SOURCE_KEYS, t),
    [t]
  );
  const workingHoursOptions = useMemo(
    () => translateOptions(WORKING_HOURS_KEYS, t),
    [t]
  );
  const roundingOptions = useMemo(
    () => translateOptions(ROUNDING_KEYS, t),
    [t]
  );
  const deductionOrderOptions = useMemo(
    () => translateOptions(DEDUCTION_ORDER_KEYS, t),
    [t]
  );
  const overtimeBaseOptions = useMemo(
    () => translateOptions(OVERTIME_BASE_KEYS, t),
    [t]
  );
  const averageMethodOptions = useMemo(
    () => translateOptions(AVG_METHOD_KEYS, t),
    [t]
  );
  const normOptions = useMemo(
    () => [
      { value: 0, label: t('ozNormNone') },
      { value: 1, label: '1' },
      { value: 2, label: '2' },
      { value: 3, label: '3' },
    ],
    [t]
  );

  const [earningCodes, setEarningCodes] = useState([]);
  const [deductionTypes, setDeductionTypes] = useState([]);
  const [otherPaymentTypes, setOtherPaymentTypes] = useState([]);
  const [earningEditIdx, setEarningEditIdx] = useState(null);
  const [deductionEditIdx, setDeductionEditIdx] = useState(null);
  const [otherPaymentEditIdx, setOtherPaymentEditIdx] = useState(null);
  // Data-layer slice — mutated by CompanyDataLayerConfig, merged into save payload.
  const [dataLayer, setDataLayer] = useState({});
  // Drives who owns the point value: the monthly card (NBS_RATE, changes every
  // month) or the plain field below (FIXED_RSD/FIXED_EUR, a constant).
  const pointValueSource = Form.useWatch('pointValueSource', form);

  useEffect(() => {
    if (config) {
      form.setFieldsValue({
        calcDirection: config.calcDirection || 'GROSS_TO_NET',
        pointValueAmount: config.pointValue?.amount ?? 1,
        pointValueSource: config.pointValue?.source || 'FIXED_RSD',
        pointValueCurrency: config.pointValue?.currency || 'RSD',
        exchangeRates: Object.entries(config.exchangeRates || {}).map(
          ([currency, rate]) => ({ currency, rate })
        ),
        workingHoursSource: config.workingHoursSource || 'CALENDAR',
        customFixedHours: config.customFixedHours,
        normalizationType: config.normalizationType ?? 0,
        minWageSupplement: config.minWageSupplement ?? 0,
        roundingSalary: config.rounding?.salary || 'ROUND',
        roundingDeductions: config.rounding?.deductions || 'ROUND',
        minBaseProportional: config.minBaseProportional ?? false,
        deductionOrder: config.deductionOrder || 'BY_PRIORITY',
        incentive1: config.incentivesEnabled?.incentive1 ?? true,
        incentive2: config.incentivesEnabled?.incentive2 ?? false,
        defaultOvpCode: config.ppppdMapping?.defaultOvpCode || '101',
        defaultContributionGroup:
          config.ppppdMapping?.defaultContributionGroup || 'D01',
        defaultPaymentCode: config.ppppdMapping?.defaultPaymentCode || '240',
        overtimeBase: config.overtimeBase || 'GROSS',
        averageCalcMethod: config.averageCalcMethod || 'HOURLY_RATE',
        minWageAvgHours: config.minWageAvgHours ?? 174,
        minWagePerHour: config.minWagePerHour,
        netTargetEnabled: !!config.netTargetEnabled,
        effectiveFrom: config.effectiveFrom
          ? dayjs(config.effectiveFrom)
          : dayjs(),
        notes: config.notes,
      });
      setEarningCodes(config.earningCodes || []);
      setDeductionTypes(config.deductionTypes || []);
      setOtherPaymentTypes(config.otherPaymentTypes || []);
      setDataLayer({
        allowanceMethods: config.allowanceMethods || {},
        topLevelNmb: config.topLevelNmb || {},
        variationMappings: config.variationMappings || {
          bonus: [],
          nmb: [],
          suspension: [],
        },
        dopunaConfig: config.dopunaConfig || {
          enabled: true,
          minGap: 5000,
          fillEarningCode: '213',
          fillEarningName: 'Razlika do ugovorene zarade',
        },
        useAbsoftTip40Formula: !!config.useAbsoftTip40Formula,
        usePerEmployeeNetCoef: !!config.usePerEmployeeNetCoef,
        nightWorkAsOvertime: !!config.nightWorkAsOvertime,
        empCoefOverride: config.empCoefOverride ?? null,
        excludeFromAverage: config.excludeFromAverage || [],
        absoft: config.absoft || {},
        statusMatrixOverrides: config.statusMatrixOverrides || [],
        earningCodeOverrides: config.earningCodeOverrides || [],
        workTypeOverrides: config.workTypeOverrides || [],
        skipCorrectionEarningCodes: config.skipCorrectionEarningCodes || [],
        sectorRules: config.sectorRules || [],
      });
    } else if (!isLoading) {
      form.setFieldsValue({
        calcDirection: 'GROSS_TO_NET',
        pointValueAmount: 1,
        pointValueSource: 'FIXED_RSD',
        pointValueCurrency: 'RSD',
        exchangeRates: [],
        workingHoursSource: 'CALENDAR',
        normalizationType: 0,
        minWageSupplement: 0,
        roundingSalary: 'ROUND',
        roundingDeductions: 'ROUND',
        minBaseProportional: false,
        deductionOrder: 'BY_PRIORITY',
        incentive1: true,
        incentive2: false,
        defaultOvpCode: '101',
        defaultContributionGroup: 'D01',
        defaultPaymentCode: '240',
        overtimeBase: 'GROSS',
        averageCalcMethod: 'HOURLY_RATE',
        minWageAvgHours: 174,
        netTargetEnabled: false,
        effectiveFrom: dayjs(),
      });
    }
  }, [config, isLoading, form]);

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      const payload = {
        calcDirection: values.calcDirection,
        pointValue: {
          amount: values.pointValueAmount,
          source: values.pointValueSource,
          // Currency is its own field, not something derived from the source.
          // It used to be forced to RSD for anything other than FIXED_EUR,
          // so saving this form on an NBS_RATE company silently flipped an
          // EUR point value to RSD — and that same field is what the engine
          // falls back to when converting EUR amounts, so one save through
          // the UI would stop CCECC, P3, UNLIMIT and both PCCC branches from
          // converting anything.
          currency:
            values.pointValueSource === 'FIXED_EUR'
              ? 'EUR'
              : values.pointValueCurrency || 'RSD',
        },
        // Rate per currency, keyed by upper-case code. The engine reads this
        // before falling back to pointValue, which is the only workable order
        // for a company whose point value is a real point value (Proctorio:
        // TYPE_02_POINTS, point value 1, bonuses and suspensions in EUR).
        exchangeRates: (values.exchangeRates || []).reduce((acc, row) => {
          const code = String(row?.currency || '').toUpperCase();
          const rate = Number(row?.rate);
          if (code && rate > 0) acc[code] = rate;
          return acc;
        }, {}),
        workingHoursSource: values.workingHoursSource,
        customFixedHours: values.customFixedHours,
        normalizationType: values.normalizationType,
        minWageSupplement: values.minWageSupplement,
        rounding: {
          salary: values.roundingSalary,
          deductions: values.roundingDeductions,
        },
        minBaseProportional: values.minBaseProportional,
        deductionOrder: values.deductionOrder,
        incentivesEnabled: {
          incentive1: values.incentive1,
          incentive2: values.incentive2,
        },
        ppppdMapping: {
          defaultOvpCode: values.defaultOvpCode,
          defaultContributionGroup: values.defaultContributionGroup,
          defaultPaymentCode: values.defaultPaymentCode,
          overrides: config?.ppppdMapping?.overrides || [],
        },
        overtimeBase: values.overtimeBase,
        averageCalcMethod: values.averageCalcMethod,
        minWageAvgHours: values.minWageAvgHours,
        minWagePerHour: values.minWagePerHour,
        netTargetEnabled: !!values.netTargetEnabled,
        effectiveFrom: values.effectiveFrom?.toISOString(),
        notes: values.notes,
        earningCodes,
        deductionTypes,
        otherPaymentTypes,
        ...dataLayer,
      };

      await updateConfig.mutateAsync({
        companyId,
        config: payload,
      });
      message.success(t('saved'));
    } catch (err) {
      if (err.errorFields) return;
      message.error(err.response?.data?.error || err.message);
    }
  };

  const earningColumns = [
    {
      title: t('code'),
      dataIndex: 'code',
      editable: true,
      render: (v) => v,
      editRender: () => (
        <Form.Item name="code" noStyle>
          <Input size="small" style={{ width: 80 }} />
        </Form.Item>
      ),
    },
    {
      title: t('name'),
      dataIndex: 'name',
      editable: true,
      render: (v) => v,
      editRender: () => (
        <Form.Item name="name" noStyle>
          <Input size="small" style={{ width: 150 }} />
        </Form.Item>
      ),
    },
    {
      title: t('ozCalcType'),
      dataIndex: 'calcType',
      editable: true,
      render: (v) => calcTypeOptions.find((o) => o.value === v)?.label || v,
      editRender: () => (
        <Form.Item name="calcType" noStyle>
          <Select
            size="small"
            style={{ width: 170 }}
            options={calcTypeOptions}
          />
        </Form.Item>
      ),
    },
    {
      title: t('ozTaxTreatment'),
      dataIndex: 'taxTreatment',
      editable: true,
      render: (v) => taxTreatmentOptions.find((o) => o.value === v)?.label || v,
      editRender: () => (
        <Form.Item name="taxTreatment" noStyle>
          <Select
            size="small"
            style={{ width: 120 }}
            options={taxTreatmentOptions}
          />
        </Form.Item>
      ),
    },
    {
      title: t('ozAbSoftType'),
      dataIndex: 'abSoftType',
      editable: true,
      render: (v) =>
        v == null ? '-' : abSoftTypeOptions.find((o) => o.value === v)?.label,
      editRender: () => (
        <Form.Item name="abSoftType" noStyle>
          <Select
            size="small"
            allowClear
            style={{ width: 130 }}
            options={abSoftTypeOptions}
          />
        </Form.Item>
      ),
    },
    {
      title: t('ozDefault'),
      dataIndex: 'isDefault',
      render: (v) => (v ? t('yes') : ''),
      editable: true,
      editRender: () => (
        <Form.Item name="isDefault" valuePropName="checked" noStyle>
          <Switch size="small" />
        </Form.Item>
      ),
    },
  ];

  const deductionColumns = [
    {
      title: t('code'),
      dataIndex: 'code',
      editable: true,
      render: (v) => v,
      editRender: () => (
        <Form.Item name="code" noStyle>
          <Input size="small" style={{ width: 80 }} />
        </Form.Item>
      ),
    },
    {
      title: t('name'),
      dataIndex: 'name',
      editable: true,
      render: (v) => v,
      editRender: () => (
        <Form.Item name="name" noStyle>
          <Input size="small" style={{ width: 150 }} />
        </Form.Item>
      ),
    },
    {
      title: t('ozCreditor'),
      dataIndex: 'creditor',
      editable: true,
      render: (v) => v || '-',
      editRender: () => (
        <Form.Item name="creditor" noStyle>
          <Input size="small" style={{ width: 150 }} />
        </Form.Item>
      ),
    },
    {
      title: t('priority'),
      dataIndex: 'priority',
      editable: true,
      render: (v) => v ?? 0,
      editRender: () => (
        <Form.Item name="priority" noStyle>
          <InputNumber size="small" min={0} style={{ width: 60 }} />
        </Form.Item>
      ),
    },
    {
      title: t('ozLimitType'),
      dataIndex: 'limitType',
      editable: true,
      render: (v) => limitTypeOptions.find((o) => o.value === v)?.label || v,
      editRender: () => (
        <Form.Item name="limitType" noStyle>
          <Select
            size="small"
            style={{ width: 120 }}
            options={limitTypeOptions}
          />
        </Form.Item>
      ),
    },
    {
      title: t('ozCategory'),
      dataIndex: 'category',
      editable: true,
      render: (v) => categoryOptions.find((o) => o.value === v)?.label || v,
      editRender: () => (
        <Form.Item name="category" noStyle>
          <Select size="small" style={{ width: 130 }} options={categoryOptions} />
        </Form.Item>
      ),
    },
    {
      title: t('ozCarryOver'),
      dataIndex: 'carryOver',
      render: (v) => (v ? t('yes') : t('no')),
      editable: true,
      editRender: () => (
        <Form.Item name="carryOver" valuePropName="checked" noStyle>
          <Switch size="small" />
        </Form.Item>
      ),
    },
  ];

  const otherPaymentColumns = [
    {
      title: t('code'),
      dataIndex: 'code',
      editable: true,
      render: (v) => v,
      editRender: () => (
        <Form.Item name="code" noStyle>
          <Input size="small" style={{ width: 80 }} />
        </Form.Item>
      ),
    },
    {
      title: t('name'),
      dataIndex: 'name',
      editable: true,
      render: (v) => v,
      editRender: () => (
        <Form.Item name="name" noStyle>
          <Input size="small" style={{ width: 150 }} />
        </Form.Item>
      ),
    },
    {
      title: t('ozTaxable'),
      dataIndex: 'taxable',
      render: (v) => (v ? t('yes') : t('no')),
      editable: true,
      editRender: () => (
        <Form.Item name="taxable" valuePropName="checked" noStyle>
          <Switch size="small" />
        </Form.Item>
      ),
    },
    {
      title: t('ozNonTaxableLimit'),
      dataIndex: 'nonTaxableLimit',
      render: (v) => (v != null ? v.toLocaleString('sr-RS') : '-'),
      editable: true,
      editRender: () => (
        <Form.Item name="nonTaxableLimit" noStyle>
          <InputNumber size="small" min={0} style={{ width: 100 }} />
        </Form.Item>
      ),
    },
    {
      title: t('ozProportional'),
      dataIndex: 'proportionalToHours',
      render: (v) => (v ? t('yes') : t('no')),
      editable: true,
      editRender: () => (
        <Form.Item name="proportionalToHours" valuePropName="checked" noStyle>
          <Switch size="small" />
        </Form.Item>
      ),
    },
    {
      title: t('ozContributions'),
      dataIndex: 'contributionsApply',
      render: (v) => (v ? t('yes') : t('no')),
      editable: true,
      editRender: () => (
        <Form.Item name="contributionsApply" valuePropName="checked" noStyle>
          <Switch size="small" />
        </Form.Item>
      ),
    },
    {
      title: t('ozAmountIsNet'),
      dataIndex: 'amountIsNet',
      render: (v) => (v ? t('yes') : t('no')),
      editable: true,
      editRender: () => (
        <Form.Item name="amountIsNet" valuePropName="checked" noStyle>
          <Switch size="small" />
        </Form.Item>
      ),
    },
  ];

  const makeArrayHandlers = (items, setItems) => ({
    onAdd: (empty) => setItems([...items, empty]),
    onRemove: (idx) => setItems(items.filter((_, i) => i !== idx)),
  });

  const makeSaveEdit = (items, setItems, editForm, setEditIdx) => {
    return (idx) => {
      const vals = editForm.getFieldsValue();
      const updated = [...items];
      updated[idx] = { ...updated[idx], ...vals };
      setItems(updated);
      setEditIdx(null);
      editForm.resetFields();
    };
  };

  if (!companyId) {
    return (
      <div className="p-8 text-center text-gray-400">{t('selectCompany')}</div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex justify-center p-8">
        <Spin />
      </div>
    );
  }

  const handleReset = () => {
    if (config) {
      form.setFieldsValue({
        calcDirection: config.calcDirection || 'GROSS_TO_NET',
        pointValueAmount: config.pointValue?.amount ?? 1,
        pointValueSource: config.pointValue?.source || 'FIXED_RSD',
        pointValueCurrency: config.pointValue?.currency || 'RSD',
        exchangeRates: Object.entries(config.exchangeRates || {}).map(
          ([currency, rate]) => ({ currency, rate })
        ),
        workingHoursSource: config.workingHoursSource || 'CALENDAR',
        customFixedHours: config.customFixedHours,
        normalizationType: config.normalizationType ?? 0,
        minWageSupplement: config.minWageSupplement ?? 0,
        roundingSalary: config.rounding?.salary || 'ROUND',
        roundingDeductions: config.rounding?.deductions || 'ROUND',
        minBaseProportional: config.minBaseProportional ?? false,
        deductionOrder: config.deductionOrder || 'BY_PRIORITY',
        incentive1: config.incentivesEnabled?.incentive1 ?? true,
        incentive2: config.incentivesEnabled?.incentive2 ?? false,
        defaultOvpCode: config.ppppdMapping?.defaultOvpCode || '101',
        defaultContributionGroup:
          config.ppppdMapping?.defaultContributionGroup || 'D01',
        defaultPaymentCode: config.ppppdMapping?.defaultPaymentCode || '240',
        overtimeBase: config.overtimeBase || 'GROSS',
        averageCalcMethod: config.averageCalcMethod || 'HOURLY_RATE',
        minWageAvgHours: config.minWageAvgHours ?? 174,
        minWagePerHour: config.minWagePerHour,
        netTargetEnabled: !!config.netTargetEnabled,
        effectiveFrom: config.effectiveFrom
          ? dayjs(config.effectiveFrom)
          : dayjs(),
        notes: config.notes,
      });
      setEarningCodes(config.earningCodes || []);
      setDeductionTypes(config.deductionTypes || []);
      setOtherPaymentTypes(config.otherPaymentTypes || []);
    }
  };

  const completeness = computeConfigCompleteness(config);
  const requiredPct = Math.round(
    (completeness.requiredFilled / completeness.requiredTotal) * 100
  );
  const optionalPct = Math.round(
    (completeness.optionalFilled / completeness.optionalTotal) * 100
  );

  return (
    <div>
      <Card size="small" className="mb-4">
        <div className="grid grid-cols-2 gap-3">
          <div>
            <div className="mb-1 flex items-center justify-between">
              <span className="text-xs font-semibold">
                {t('ozConfigComplete')} (obavezno)
              </span>
              <Tag
                color={
                  completeness.requiredFilled === completeness.requiredTotal
                    ? 'green'
                    : 'red'
                }
              >
                {completeness.requiredFilled} / {completeness.requiredTotal}
              </Tag>
            </div>
            <Progress
              percent={requiredPct}
              status={requiredPct === 100 ? 'success' : 'exception'}
              size="small"
            />
            {completeness.missingRequired.length > 0 && (
              <div className="mt-1 text-xs text-red-500">
                {t('ozConfigMissing')}:{' '}
                {completeness.missingRequired.join(', ')}
              </div>
            )}
          </div>
          <div>
            <div className="mb-1 flex items-center justify-between">
              <span className="text-xs font-semibold">
                {t('ozConfigComplete')} (preporučeno)
              </span>
              <Tag color={optionalPct === 100 ? 'green' : 'blue'}>
                {completeness.optionalFilled} / {completeness.optionalTotal}
              </Tag>
            </div>
            <Progress
              percent={optionalPct}
              status="active"
              size="small"
            />
          </div>
        </div>
      </Card>

      <MonthParamsCard
        ia31={dataLayer?.absoft?.ia31}
        onChange={(next) =>
          setDataLayer((prev) => ({
            ...prev,
            absoft: { ...(prev.absoft || {}), ia31: next },
          }))
        }
        pointValueSource={pointValueSource}
        onPointValueChange={(amount) =>
          form.setFieldsValue({ pointValueAmount: amount })
        }
      />

      <Form form={form} layout="vertical" size="small">
        <div className="grid grid-cols-2 gap-x-5">
          <Form.Item label={t('ozCalcDirection')} name="calcDirection">
            <Select options={calcDirectionOptions} />
          </Form.Item>

          {/* With NBS_RATE the point value changes every month, so it is owned by
              the monthly card above; keeping it here too is what let the same
              number live in two places and drift. */}
          <Form.Item
            label={t('ozPointValue')}
            name="pointValueAmount"
            hidden={pointValueSource === 'NBS_RATE'}
          >
            <InputNumber className="w-full" min={0} step={0.01} />
          </Form.Item>

          <Form.Item label={t('ozPointValueSource')} name="pointValueSource">
            <Select options={pointValueSourceOptions} />
          </Form.Item>

          <Form.Item
            label={t('ozPointValueCurrency')}
            name="pointValueCurrency"
          >
            <Select
              options={[
                { value: 'RSD', label: 'RSD' },
                ...CURRENCY_OPTIONS,
              ]}
            />
          </Form.Item>

          <Form.Item label={t('ozExchangeRates')} className="col-span-2">
            <div className="mb-2 text-xs text-gray-500">
              {t('ozExchangeRatesHint')}
            </div>
            <Form.List name="exchangeRates">
              {(fields, { add, remove }) => (
                <>
                  {fields.map(({ key, name, ...rest }) => (
                    <Space key={key} align="baseline" className="mb-1 flex">
                      <Form.Item
                        {...rest}
                        name={[name, 'currency']}
                        rules={[{ required: true }]}
                        className="mb-0"
                      >
                        <Select
                          className="w-28"
                          placeholder={t('ozExchangeRateCurrency')}
                          options={CURRENCY_OPTIONS}
                        />
                      </Form.Item>
                      <Form.Item
                        {...rest}
                        name={[name, 'rate']}
                        rules={[{ required: true }]}
                        className="mb-0"
                      >
                        <InputNumber
                          className="w-40"
                          min={0}
                          step={0.0001}
                          placeholder={t('ozExchangeRateValue')}
                        />
                      </Form.Item>
                      <Button
                        size="small"
                        danger
                        icon={<DeleteOutlined />}
                        onClick={() => remove(name)}
                      />
                    </Space>
                  ))}
                  <Button
                    size="small"
                    icon={<PlusOutlined />}
                    onClick={() => add({ currency: undefined, rate: undefined })}
                  >
                    {t('ozExchangeRateAdd')}
                  </Button>
                </>
              )}
            </Form.List>
          </Form.Item>

          <Form.Item label={t('ozWorkingHours')} name="workingHoursSource">
            <Select options={workingHoursOptions} />
          </Form.Item>

          {workingHoursSource === 'CUSTOM' && (
            <Form.Item label={t('ozCustomHours')} name="customFixedHours">
              <InputNumber className="w-full" min={1} max={260} />
            </Form.Item>
          )}

          <Form.Item label={t('ozNormalization')} name="normalizationType">
            <Select options={normOptions} />
          </Form.Item>

          <Form.Item label={t('ozMinWageSupplement')} name="minWageSupplement">
            <InputNumber className="w-full" min={0} max={10} />
          </Form.Item>

          <Form.Item label={t('ozRoundingSalary')} name="roundingSalary">
            <Select options={roundingOptions} />
          </Form.Item>

          <Form.Item
            label={t('ozRoundingDeductions')}
            name="roundingDeductions"
          >
            <Select options={roundingOptions} />
          </Form.Item>
        </div>

        <div className="grid grid-cols-2 gap-x-5">
          <Form.Item label={t('ozDeductionOrder')} name="deductionOrder">
            <Select options={deductionOrderOptions} />
          </Form.Item>

          <Form.Item
            label={t('ozMinBaseProportional')}
            name="minBaseProportional"
            valuePropName="checked"
          >
            <Switch />
          </Form.Item>
        </div>

        <Divider className="my-3" />

        <div className="grid grid-cols-2 gap-x-5">
          <Form.Item
            label={t('ozIncentive1')}
            name="incentive1"
            valuePropName="checked"
          >
            <Switch />
          </Form.Item>

          <Form.Item
            label={t('ozIncentive2')}
            name="incentive2"
            valuePropName="checked"
          >
            <Switch />
          </Form.Item>
        </div>

        <Divider className="my-3" />

        <h4 className="mb-2 font-semibold">PPPPD {t('ozDefaults')}</h4>
        <div className="grid grid-cols-2 gap-x-5">
          <Form.Item label="OVP" name="defaultOvpCode">
            <Input />
          </Form.Item>
          <Form.Item
            label={t('ozContributionGroup')}
            name="defaultContributionGroup"
          >
            <Input />
          </Form.Item>
          <Form.Item label={t('ozPaymentCode')} name="defaultPaymentCode">
            <Input />
          </Form.Item>
        </div>

        <Divider className="my-3" />

        <h4 className="mb-2 font-semibold">{t('ozOvertimeBase')} / {t('ozAverageCalcMethod')}</h4>
        <div className="grid grid-cols-2 gap-x-5">
          <Form.Item label={t('ozOvertimeBase')} name="overtimeBase">
            <Select options={overtimeBaseOptions} />
          </Form.Item>
          <Form.Item
            label={t('ozAverageCalcMethod')}
            name="averageCalcMethod"
          >
            <Select options={averageMethodOptions} />
          </Form.Item>
          <Form.Item label={t('ozMinWageAvgHours')} name="minWageAvgHours">
            <InputNumber className="w-full" min={0} />
          </Form.Item>
          <Form.Item label={t('ozMinWagePerHour')} name="minWagePerHour">
            <InputNumber className="w-full" min={0} step={0.01} />
          </Form.Item>
          <Form.Item
            label={t('ozNetTargetEnabled')}
            name="netTargetEnabled"
            valuePropName="checked"
          >
            <Switch />
          </Form.Item>
        </div>

        <Divider className="my-3" />

        <div className="grid grid-cols-2 gap-x-5">
          <Form.Item
            label={t('effectiveFrom')}
            name="effectiveFrom"
            rules={[{ required: true }]}
          >
            <DatePicker className="w-full" />
          </Form.Item>

          <Form.Item label={t('notes')} name="notes">
            <Input.TextArea rows={2} />
          </Form.Item>
        </div>
      </Form>

      <Divider className="my-3" />

      <Form form={earningEditForm} component={false}>
        <ArrayCrud
          confirmText={t('confirmDelete')}
          title={t('ozEarningCodes')}
          items={earningCodes}
          columns={earningColumns}
          emptyItem={{
            code: '',
            name: '',
            calcType: 'TYPE_02_POINTS',
            taxTreatment: 'FULLY_TAXABLE',
            isDefault: false,
          }}
          editingIndex={earningEditIdx}
          setEditingIndex={(idx) => {
            setEarningEditIdx(idx);
            if (idx !== null) earningEditForm.setFieldsValue(earningCodes[idx]);
          }}
          editForm={earningEditForm}
          {...makeArrayHandlers(earningCodes, setEarningCodes)}
          onSaveEdit={makeSaveEdit(
            earningCodes,
            setEarningCodes,
            earningEditForm,
            setEarningEditIdx
          )}
        />
      </Form>

      <Form form={deductionEditForm} component={false}>
        <ArrayCrud
          confirmText={t('confirmDelete')}
          title={t('ozDeductionTypes')}
          items={deductionTypes}
          columns={deductionColumns}
          emptyItem={{
            code: '',
            name: '',
            creditor: '',
            priority: 0,
            limitType: 'NONE',
            category: 'VOLUNTARY',
            carryOver: false,
            isTaxDeductible: false,
          }}
          editingIndex={deductionEditIdx}
          setEditingIndex={(idx) => {
            setDeductionEditIdx(idx);
            if (idx !== null)
              deductionEditForm.setFieldsValue(deductionTypes[idx]);
          }}
          editForm={deductionEditForm}
          {...makeArrayHandlers(deductionTypes, setDeductionTypes)}
          onSaveEdit={makeSaveEdit(
            deductionTypes,
            setDeductionTypes,
            deductionEditForm,
            setDeductionEditIdx
          )}
        />
      </Form>

      <Form form={otherPaymentEditForm} component={false}>
        <ArrayCrud
          confirmText={t('confirmDelete')}
          title={t('ozOtherPayments')}
          items={otherPaymentTypes}
          columns={otherPaymentColumns}
          emptyItem={{
            code: '',
            name: '',
            taxable: true,
            nonTaxableLimit: null,
            proportionalToHours: true,
            contributionsApply: false,
            amountIsNet: false,
          }}
          editingIndex={otherPaymentEditIdx}
          setEditingIndex={(idx) => {
            setOtherPaymentEditIdx(idx);
            if (idx !== null)
              otherPaymentEditForm.setFieldsValue(otherPaymentTypes[idx]);
          }}
          editForm={otherPaymentEditForm}
          {...makeArrayHandlers(otherPaymentTypes, setOtherPaymentTypes)}
          onSaveEdit={makeSaveEdit(
            otherPaymentTypes,
            setOtherPaymentTypes,
            otherPaymentEditForm,
            setOtherPaymentEditIdx
          )}
        />
      </Form>

      <OtherPaymentsSectorOverrides
        items={otherPaymentTypes}
        onChange={setOtherPaymentTypes}
      />

      <Divider className="my-3" />

      <CompanyDataLayerConfig
        config={dataLayer}
        onChange={(patch) => setDataLayer((prev) => ({ ...prev, ...patch }))}
      />

      <div className="mt-4 flex justify-end gap-2">
        <Button
          type="default"
          disabled={updateConfig.isPending}
          className="btn-default btn-default--hover"
          onClick={handleReset}
        >
          {t('reset')}
        </Button>
        <Button
          type="primary"
          loading={updateConfig.isPending}
          disabled={updateConfig.isPending}
          className="btn-primary"
          onClick={handleSave}
        >
          {t('save')}
        </Button>
      </div>
    </div>
  );
}
